import React, { useCallback } from 'react';
import { Col } from 'reactstrap';
import i18next from 'i18next';
import { toast } from 'react-toastify';

import { NormalizedRestaurant } from 'types';
import ClosedDaysForm, {
  formRuleToApiRule,
  ClosedDaysFormValues,
} from 'app/components/ClosedDaysForm';
import ErrorBoundary from '../containers/ErrorBoundary';
import { col10Style, col8Style } from '../styles';
import type { PageComponent } from '../types-global';
import { useAppSelector, useAppDispatch } from 'hooks/useRedux';
import { selectRestaurant, setRestaurantSuccess } from 'store/restaurant';
import UpdatesProvider from 'app/containers/UpdatesProvider';
import { UpdateTarget } from 'enums';
import { hasReservations } from 'utils/restaurant';
import PreviewLink, { RestaurantPreviewType } from 'app/views/ViewPreviewLink';
import { put } from 'services/api';
import { setRestaurant } from 'services/persistent';
import { logActivity, stringifyEntity, logError } from 'utils/log';

const SpecialDaysSettings: PageComponent<{}> = () => {
  const dispatch = useAppDispatch();
  const restaurant = useAppSelector(selectRestaurant) as NormalizedRestaurant;
  const { custom_rules: rules, name } = restaurant;

  const handleSubmit = useCallback(
    async (values: ClosedDaysFormValues) => {
      const apiRules = values.rules.map(formRuleToApiRule);
      const url = `/restaurants/${restaurant.id}`;
      const updated = await put(url, { custom_rules: apiRules });
      setRestaurant(updated);
      dispatch(setRestaurantSuccess(updated));
      toast.success(i18next.t('special-days-settings.save-success'));
      logActivity(
        `Dni wyjątkowe zostały zaktualizowane.\n ${stringifyEntity(apiRules)}`
      );
    },
    [restaurant.id, dispatch]
  );

  return (
    <>
      <UpdatesProvider
        restaurant={restaurant}
        target={UpdateTarget.CustomRules}
      >
        <Col lg={col10Style} xl={col8Style}>
          <PreviewLink type={hasReservations(restaurant) ? RestaurantPreviewType.Resevations : RestaurantPreviewType.Menu} restaurant={restaurant}/>
          <h3 className="h4 mt-2">{i18next.t('special-days-settings.title', { name })}</h3>
          <ErrorBoundary componentName="ClosedDaysForm">
            <ClosedDaysForm
              hasReservations={hasReservations(restaurant)}
              rules={
                rules && rules.filter
                  ? rules.filter((rule) => rule.key === 'visitDate')
                  : null
              }
              onSubmit={(values) =>
                handleSubmit(values).catch((e) => {
                  logError('ClosedDaysForm update fail', e);
                  toast.error(i18next.t('special-days-settings.save-error'));
                  throw e;
                })
              }
            />
          </ErrorBoundary>
        </Col>
      </UpdatesProvider>
    </>
  );
};

SpecialDaysSettings.navName = 'Dni wyjątkowe';
SpecialDaysSettings.url = '/settings/special-days';

SpecialDaysSettings.displayName = 'SpecialDaysSettings';

export default SpecialDaysSettings;
